import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage} from 'react-intl';
import MonacoEditor from 'react-monaco-editor';

import styles from './python-compiler-tab.css';

class PythonCompilerTab extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            code: 'print("Hello")',
            output: '',
            isError: false,
            isSkulptLoaded: false,
            isWaitingForInput: false,
            inputValue: '',
            isConsoleExpanded: false
        };
        this.pendingInputResolver = null;
        this.handleCodeChange = this.handleCodeChange.bind(this);
        this.runPythonCode = this.runPythonCode.bind(this);
        this.clearOutput = this.clearOutput.bind(this);
        this.editorDidMount = this.editorDidMount.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleInputSubmit = this.handleInputSubmit.bind(this);
        this.toggleConsoleExpand = this.toggleConsoleExpand.bind(this);
    }

    componentDidMount () {
        if (!window.Sk) {
            // Load Skulpt dynamically
            const script = document.createElement("script");
            script.src = "https://cdn.jsdelivr.net/npm/skulpt@1.2.0/dist/skulpt.min.js";
            script.onload = () => {
                const scriptStd = document.createElement("script");
                scriptStd.src = "https://cdn.jsdelivr.net/npm/skulpt@1.2.0/dist/skulpt-stdlib.js";
                scriptStd.onload = () => {
                    this.setState({ isSkulptLoaded: true });
                };
                document.body.appendChild(scriptStd);
            };
            document.body.appendChild(script);
        } else {
            this.setState({ isSkulptLoaded: true });
        }
    }

    handleCodeChange (newValue) {
        this.setState({code: newValue});
    }

    toggleConsoleExpand () {
        this.setState(prevState => ({ isConsoleExpanded: !prevState.isConsoleExpanded }));
    }

    handleInputChange (e) {
        this.setState({ inputValue: e.target.value });
    }

    handleInputSubmit (e) {
        if (e.key === 'Enter') {
            const val = this.state.inputValue;
            this.setState(prevState => ({
                output: prevState.output + val + "\n",
                isWaitingForInput: false,
                inputValue: ''
            }));
            if (this.pendingInputResolver) {
                this.pendingInputResolver(val);
                this.pendingInputResolver = null;
            }
        }
    }

    editorDidMount (editor, monaco) {
        editor.focus();
    }

    clearOutput () {
        this.setState({ output: '', isError: false });
    }

    runPythonCode () {
        if (!window.Sk || !this.state.isSkulptLoaded) {
            this.setState({ output: "Python engine is still loading. Please try again in a moment.", isError: true });
            return;
        }

        this.clearOutput();
        const code = this.state.code;
        const Sk = window.Sk;

        // Skulpt configuration
        Sk.configure({
            output: (text) => {
                this.setState(prevState => ({
                    output: prevState.output + text
                }));
            },
            read: (x) => {
                if (Sk.builtinFiles === undefined || Sk.builtinFiles.files[x] === undefined) {
                    throw "File not found: '" + x + "'";
                }
                return Sk.builtinFiles.files[x];
            },
            inputfun: (promptText) => {
                return new Promise((resolve) => {
                    this.setState(prevState => ({
                        output: prevState.output + promptText,
                        isWaitingForInput: true,
                        inputValue: ''
                    }));
                    this.pendingInputResolver = resolve;
                });
            }
        });

        // Run Skulpt
        const myPromise = Sk.misceval.asyncToPromise(() => {
            return Sk.importMainWithBody("<stdin>", false, code, true);
        });

        myPromise.then(
            () => {
                // Success
            },
            (err) => {
                // Error
                this.setState(prevState => ({
                    output: prevState.output + err.toString() + "\n",
                    isError: true
                }));
            }
        );
    }

    render () {
        return (
            <div className={styles.pythonCompilerTab}>
                <div className={styles.header}>
                    <FormattedMessage
                        defaultMessage="Python Compiler"
                        description="Header for the Python compiler tab"
                        id="gui.pythonCompilerTab.header"
                    />
                    <button 
                        className={styles.runButton} 
                        onClick={this.runPythonCode}
                        disabled={!this.state.isSkulptLoaded}
                    >
                        <FormattedMessage
                            defaultMessage="Run Code"
                            description="Button to execute Python code"
                            id="gui.pythonCompilerTab.runCode"
                        />
                    </button>
                </div>
                <div className={styles.workspace}>
                    <div 
                        className={styles.editorContainer} 
                        style={{ display: this.state.isConsoleExpanded ? 'none' : 'flex' }}
                    >
                        <MonacoEditor
                            width="100%"
                            height="100%"
                            language="python"
                            theme="vs-light"
                            value={this.state.code}
                            options={{
                                selectOnLineNumbers: true,
                                minimap: { enabled: false }
                            }}
                            onChange={this.handleCodeChange}
                            editorDidMount={this.editorDidMount}
                        />
                    </div>
                    <div className={this.state.isConsoleExpanded ? `${styles.consoleContainer} ${styles.expanded}` : styles.consoleContainer}>
                        <div className={styles.consoleHeader}>
                            <span>Terminal Output</span>
                            <button className={styles.expandButton} onClick={this.toggleConsoleExpand}>
                                {this.state.isConsoleExpanded ? "Collapse" : "Expand"}
                            </button>
                        </div>
                        <div className={styles.consoleBody}>
                            <pre className={this.state.isError ? styles.consoleError : styles.consoleOutput}>
                                {this.state.output || "Output will appear here..."}
                            </pre>
                            {this.state.isWaitingForInput && (
                                <div className={styles.inputWrapper}>
                                    <span className={styles.inputPrompt}>&gt;</span>
                                    <input
                                        type="text"
                                        className={styles.inlineInput}
                                        value={this.state.inputValue}
                                        onChange={this.handleInputChange}
                                        onKeyDown={this.handleInputSubmit}
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

PythonCompilerTab.propTypes = {
};

export default PythonCompilerTab;
